import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { GitCompare } from "lucide-react";
import { toast } from "sonner";
import { Unlock } from "@/pages/RealPortfolio";
import { CountCards, ComparisonList, SeparationLine, RowsTable } from "@/components/ledgercompare/LedgerCompareParts";

export default function LedgerCompare() {
  const [grant, setGrant] = useState(null);
  const [ttl, setTtl] = useState(0);
  useEffect(() => {
    if (!grant) { return undefined; }
    const t = setTimeout(() => setGrant(null), ttl * 1000);
    return () => clearTimeout(t);
  }, [grant, ttl]);
  const lock = async () => {
    try {
      await api.post("/portfolio/real/lock", {}, { headers: { "X-Portfolio-Grant": grant } });
    } catch (e) {
      toast.error("Lock request failed; details hidden locally and expire within 10 min");
    } finally { setGrant(null); }
  };
  const { data: s, isLoading, isError, refetch } = useQuery({ queryKey: ["lc-summary"], gcTime: 0, queryFn: async () => (await api.get("/ledger-compare/summary")).data });
  return (
    <div data-testid="ledger-compare-page" className="space-y-4">
      <PageHeader title="Ledger Comparison" subtitle="Advisory comparison of verified Sharekhan records with your real Tradify records — never creates, edits or corrects ledger entries" icon={GitCompare} demo={false} testId="ledger-compare-header" />
      {isLoading && <LoadingState label="Comparing…" />}
      {isError && <ErrorState label="Comparison could not be loaded" onRetry={refetch} />}
      {s && (
        <>
          <div data-testid="lc-flags" className="flex flex-wrap gap-x-4 text-[11px] text-slate-400">
            <span data-testid="lc-status">Status: <b className={s.status === "action_required" ? "text-rose-300" : "text-emerald-300"}>{s.status === "action_required" ? "Review discrepancies" : "No discrepancies"}</b></span>
            <span data-testid="lc-positions">Positions: <b className="text-amber-300">UNVERIFIED (excluded)</b></span>
            <span data-testid="lc-advisory">Advisory only · Corrections possible: no · Orders possible: no</span>
          </div>
          <CountCards s={s} />
          <Panel title="What is compared" testId="lc-comparisons-panel"><div className="p-4 space-y-3"><ComparisonList s={s} /><SeparationLine s={s} /></div></Panel>
          <Panel title="Comparison details" testId="lc-rows-panel" actions={grant && <button data-testid="lc-lock" onClick={lock} className="text-xs text-slate-300 underline underline-offset-2 hover:text-white">Lock details</button>}>
            <div className="p-4">{grant ? <RowsTable grant={grant} /> : <Unlock onGrant={(g, t) => { setTtl(t); setGrant(g); }} />}</div>
          </Panel>
        </>
      )}
    </div>
  );
}
